import { useState } from 'react'
import { Link } from 'react-router-dom'
import '../styles/App.css'

function Help() {
  const [activeSection, setActiveSection] = useState('getting-started')

  const sections = [
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'viewer-commands', label: 'Viewer Commands' },
    { id: 'plant-guide', label: 'Plant Guide' },
    { id: 'insect-guide', label: 'Insect Guide' },
    { id: 'interactions', label: 'Interactions' },
    { id: 'bits-monetization', label: 'Bits & Shop' },
    { id: 'admin-commands', label: 'Admin & Mod' },
    { id: 'obs-setup', label: 'OBS Setup' },
    { id: 'display-settings', label: 'Display Settings' },
    { id: 'faq', label: 'FAQ' },
    { id: 'changelog', label: 'Changelog' }
  ]

  const plantTypes = [
    { name: 'Flower', desc: 'Classic bloom with colorful petals' },
    { name: 'Mushroom', desc: 'A cute woodland mushroom' },
    { name: 'Cactus', desc: 'A spiky desert cactus' },
    { name: 'Sunflower', desc: 'Tall and bright sunflower' },
    { name: 'Tulip', desc: 'A colorful tulip' },
    { name: 'Rose', desc: 'An elegant rose' },
    { name: 'Tree', desc: 'A sturdy growing tree' },
    { name: 'Fern', desc: 'A leafy green fern' },
    { name: 'Succulent', desc: 'A hardy succulent' },
    { name: 'Bamboo', desc: 'Fast-growing bamboo' },
    { name: 'Vine', desc: 'A climbing vine' },
    { name: 'Bonsai', desc: 'A miniature bonsai tree' }
  ]

  const legendaryPlantTypes = [
    { name: 'Cherry Blossom', desc: 'Exclusive cherry blossom tree with falling petals', bits: 500 },
    { name: 'Crystal Tree', desc: 'Shimmering crystal tree with prismatic glow', bits: 500 },
    { name: 'Venus Flytrap', desc: 'Carnivorous plant with snapping trap heads', bits: 500 }
  ]

  const legendaryInsectTypes = [
    { name: 'Moth', desc: 'A patterned moth with dust particles', canFly: true, bits: 500 },
    { name: 'Mantis', desc: 'A praying mantis with animated claws', canFly: false, bits: 500 },
    { name: 'Scorpion', desc: 'A scorpion with a pulsing red stinger', canFly: false, bits: 500 }
  ]

  const insectTypes = [
    { name: 'Ladybug', desc: 'A cute red ladybug that crawls around', canFly: false },
    { name: 'Butterfly', desc: 'A colorful butterfly that flutters in the air', canFly: true },
    { name: 'Bee', desc: 'A busy bee that buzzes from plant to plant', canFly: true },
    { name: 'Caterpillar', desc: 'A wiggly caterpillar that crawls along', canFly: false },
    { name: 'Firefly', desc: 'A glowing firefly that lights up at night', canFly: true },
    { name: 'Beetle', desc: 'A sturdy beetle that walks around', canFly: false },
    { name: 'Dragonfly', desc: 'A speedy dragonfly that zips through the air', canFly: true },
    { name: 'Snail', desc: 'A slow and steady snail', canFly: false },
    { name: 'Worm', desc: 'A wiggly pink worm', canFly: false }
  ]

  return (
    <div className="help-page">
      {/* Navigation */}
      <nav className="help-nav">
        <div className="help-nav-inner">
          <Link to="/" className="help-back-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Home
          </Link>
          <span className="help-brand">Stream Terrarium Help</span>
        </div>
      </nav>

      <div className="help-layout">
        {/* Sidebar */}
        <aside className="help-sidebar">
          <h3>Guide</h3>
          <ul>
            {sections.map((section) => (
              <li key={section.id}>
                <button
                  className={`sidebar-link ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  {section.label}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Content */}
        <main className="help-content">
          {activeSection === 'getting-started' && (
            <article>
              <h1>Getting Started with Stream Terrarium</h1>
              <p>
                Stream Terrarium is a <strong>free interactive Twitch overlay</strong> where your viewers can grow plants,
                adopt cute insects, and build a living terrarium together using simple chat commands.
              </p>

              <h2>Quick Setup (Under 60 Seconds)</h2>
              <ol className="help-steps">
                <li>
                  <strong>Connect your Twitch account</strong> — Click "Launch App" on the home page and
                  authorize with Twitch. We only need basic permissions and never access your stream key.
                </li>
                <li>
                  <strong>Copy your overlay URL</strong> — After logging in, you'll see your unique terrarium URL.
                  It looks like: <code>yourdomain.com/overlay/your_username</code>
                </li>
                <li>
                  <strong>Add to OBS as a Browser Source</strong> — In OBS, add a new Browser Source, paste your URL,
                  set the width to your stream width (e.g. 1920) and height to ~500px. The terrarium sits at the bottom of your stream.
                </li>
              </ol>

              <div className="help-tip">
                <strong>Tip:</strong> Make sure "Shutdown source when not visible" is unchecked in OBS so the terrarium
                stays connected even when switching scenes.
              </div>

              <h2>How It Works</h2>
              <p>
                Once the overlay is running, viewers type commands in your Twitch chat to interact with the terrarium.
                Each viewer can be either a <strong>plant</strong> or an <strong>insect</strong> — they choose when they join!
              </p>
              <ul>
                <li>Plants grow over time in the soil, watered by the community</li>
                <li>Insects roam around the terrarium, visiting plants and other bugs</li>
                <li>Chat bubbles appear above entities when viewers type in chat</li>
                <li>The terrarium persists between streams — plants keep growing 24/7</li>
              </ul>
            </article>
          )}

          {activeSection === 'viewer-commands' && (
            <article>
              <h1>Viewer Commands</h1>
              <p>These commands can be used by anyone in Twitch chat (unless subscription gating is enabled).</p>

              <h2>Joining & Leaving</h2>
              <table className="help-table">
                <thead>
                  <tr><th>Command</th><th>Description</th><th>Example</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!join plant [type]</code></td><td>Join the terrarium as a plant</td><td><code>!join plant sunflower</code></td></tr>
                  <tr><td><code>!join insect [type]</code></td><td>Join the terrarium as an insect</td><td><code>!join insect butterfly</code></td></tr>
                  <tr><td><code>!join [type]</code></td><td>Smart join — auto-detects plant or insect</td><td><code>!join butterfly</code> or <code>!join rose</code></td></tr>
                  <tr><td><code>!leave</code></td><td>Remove yourself from the terrarium</td><td><code>!leave</code></td></tr>
                </tbody>
              </table>

              <h2>Plant Commands</h2>
              <table className="help-table">
                <thead>
                  <tr><th>Command</th><th>Description</th><th>Example</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!water</code></td><td>Water your own plant</td><td><code>!water</code></td></tr>
                  <tr><td><code>!water @user</code></td><td>Water someone else's plant</td><td><code>!water @nightowl99</code></td></tr>
                  <tr><td><code>!bloom</code></td><td>Bloom your plant (at 100% growth)</td><td><code>!bloom</code></td></tr>
                  <tr><td><code>!harvest</code></td><td>Harvest your fully grown plant</td><td><code>!harvest</code></td></tr>
                  <tr><td><code>!shake</code></td><td>Shake your plant</td><td><code>!shake</code></td></tr>
                  <tr><td><code>!wave</code></td><td>Wave your plant at everyone</td><td><code>!wave</code></td></tr>
                </tbody>
              </table>

              <h2>Insect Commands</h2>
              <table className="help-table">
                <thead>
                  <tr><th>Command</th><th>Description</th><th>Example</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!fly</code></td><td>Fly to a random spot (flying insects)</td><td><code>!fly</code></td></tr>
                  <tr><td><code>!land [@user]</code></td><td>Land on a plant</td><td><code>!land @rosegrower</code></td></tr>
                  <tr><td><code>!pollinate @user</code></td><td>Pollinate a plant (gives growth boost)</td><td><code>!pollinate @gardenfan</code></td></tr>
                  <tr><td><code>!visit @user</code></td><td>Visit another entity</td><td><code>!visit @mybeepal</code></td></tr>
                  <tr><td><code>!dance</code></td><td>Make your insect dance</td><td><code>!dance</code></td></tr>
                  <tr><td><code>!pet [@user]</code></td><td>Pet your or someone else's insect</td><td><code>!pet @bugfriend</code></td></tr>
                  <tr><td><code>!sing</code></td><td>Make your insect sing (music notes appear)</td><td><code>!sing</code></td></tr>
                  <tr><td><code>!nuzzle @user</code></td><td>Nuzzle up to another entity</td><td><code>!nuzzle @plantpal</code></td></tr>
                  <tr><td><code>!sting @user</code></td><td>Sting another entity (bees only)</td><td><code>!sting @rosegrower</code></td></tr>
                  <tr><td><code>!birthdayhat @user</code></td><td>Give an insect a birthday hat (24h)</td><td><code>!birthdayhat @bugfriend</code></td></tr>
                  <tr><td><code>!tophat @user</code></td><td>Give an insect a top hat (24h)</td><td><code>!tophat @bugfriend</code></td></tr>
                  <tr><td><code>!crown @user</code></td><td>Give an insect a crown (24h)</td><td><code>!crown @bugfriend</code></td></tr>
                  <tr><td><code>!santahat @user</code></td><td>Give an insect a santa hat (24h)</td><td><code>!santahat @bugfriend</code></td></tr>
                  <tr><td><code>!cowboyhat @user</code></td><td>Give an insect a cowboy hat (24h)</td><td><code>!cowboyhat @bugfriend</code></td></tr>
                  <tr><td><code>!wizardhat @user</code></td><td>Give an insect a wizard hat (24h)</td><td><code>!wizardhat @bugfriend</code></td></tr>
                  <tr><td><code>!removehat</code></td><td>Remove your insect's hat</td><td><code>!removehat</code></td></tr>
                  <tr><td><code>!wave</code></td><td>Wave at everyone (insect dances)</td><td><code>!wave</code></td></tr>
                </tbody>
              </table>

              <h2>Info Commands</h2>
              <table className="help-table">
                <thead>
                  <tr><th>Command</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!plants</code></td><td>List all available plant types (including legendaries)</td></tr>
                  <tr><td><code>!insects</code></td><td>List all available insect types (including legendaries)</td></tr>
                  <tr><td><code>!help</code></td><td>Show all available commands in chat</td></tr>
                  <tr><td><code>!terrarium</code></td><td>What is Stream Terrarium? Quick intro in chat</td></tr>
                  <tr><td><code>!commands</code></td><td>Full list of all commands</td></tr>
                </tbody>
              </table>

              <div className="help-tip">
                <strong>Chat Bubbles:</strong> Any regular message from a viewer who has an entity in the terrarium
                will appear as a chat bubble above their plant or insect!
              </div>
            </article>
          )}

          {activeSection === 'plant-guide' && (
            <article>
              <h1>Plant Guide</h1>
              <p>
                There are <strong>12 plant species</strong> available. Each plant grows over time and can be
                watered by the community to speed up growth.
              </p>

              <h2>Available Plants (12 standard)</h2>
              <div className="help-card-grid">
                {plantTypes.map((plant) => (
                  <div key={plant.name} className="help-card">
                    <h3>{plant.name}</h3>
                    <p>{plant.desc}</p>
                    <code>!join plant {plant.name.toLowerCase()}</code>
                  </div>
                ))}
              </div>

              <h2>Legendary Plants (500 Bits each)</h2>
              <p>These exclusive plant types are unlocked by using Bits through the Terrarium extension panel.</p>
              <div className="help-card-grid">
                {legendaryPlantTypes.map((plant) => (
                  <div key={plant.name} className="help-card" style={{ borderColor: 'rgba(255, 215, 0, 0.3)' }}>
                    <h3>{plant.name} <span style={{ color: '#ffd700', fontSize: '0.75em' }}>LEGENDARY</span></h3>
                    <p>{plant.desc}</p>
                    <code>!join plant {plant.name.toLowerCase().replace(/ /g, '_')}</code>
                  </div>
                ))}
              </div>

              <h2>Plant Growth</h2>
              <ul>
                <li>Plants start at 0% and grow automatically over time</li>
                <li>Watering speeds up growth and restores health</li>
                <li>At 100% growth, use <code>!bloom</code> for a special animation</li>
                <li>Use <code>!harvest</code> to harvest mature plants and make room for new ones</li>
                <li>Plants lose health if not watered — keep the community engaged!</li>
              </ul>
            </article>
          )}

          {activeSection === 'insect-guide' && (
            <article>
              <h1>Insect Guide</h1>
              <p>
                There are <strong>9 insect types</strong> available. Insects roam around the terrarium
                autonomously and can interact with plants and other insects.
              </p>

              <h2>Available Insects (9 standard)</h2>
              <div className="help-card-grid">
                {insectTypes.map((insect) => (
                  <div key={insect.name} className="help-card">
                    <h3>{insect.name} {insect.canFly ? '(flies)' : '(crawls)'}</h3>
                    <p>{insect.desc}</p>
                    <code>!join insect {insect.name.toLowerCase()}</code>
                  </div>
                ))}
              </div>

              <h2>Legendary Insects (500 Bits each)</h2>
              <p>Exclusive insect types unlocked by using Bits through the Terrarium extension panel.</p>
              <div className="help-card-grid">
                {legendaryInsectTypes.map((insect) => (
                  <div key={insect.name} className="help-card" style={{ borderColor: 'rgba(255, 215, 0, 0.3)' }}>
                    <h3>{insect.name} {insect.canFly ? '(flies)' : '(crawls)'} <span style={{ color: '#ffd700', fontSize: '0.75em' }}>LEGENDARY</span></h3>
                    <p>{insect.desc}</p>
                    <code>!join insect {insect.name.toLowerCase()}</code>
                  </div>
                ))}
              </div>

              <h2>Flying vs Crawling</h2>
              <ul>
                <li><strong>Flying insects</strong> (butterfly, bee, firefly, dragonfly, moth) can use the <code>!fly</code> command to soar around</li>
                <li><strong>Crawling insects</strong> (ladybug, caterpillar, beetle, snail, worm, mantis, scorpion) walk along the ground</li>
                <li>All insects roam automatically between movements</li>
                <li>Insects can <code>!pollinate</code> plants to help them grow faster</li>
              </ul>
            </article>
          )}

          {activeSection === 'interactions' && (
            <article>
              <h1>Interactions</h1>
              <p>Stream Terrarium is all about community interaction. Here's what viewers can do together:</p>

              <h2>Watering</h2>
              <p>
                Any viewer can water any plant using <code>!water @username</code>. Watering restores health
                and speeds up growth. A water animation plays when a plant is watered.
              </p>

              <h2>Pollinating</h2>
              <p>
                Insect users can pollinate plants using <code>!pollinate @username</code>. This gives the plant
                a growth boost — it's like a super-water from a bug friend!
              </p>

              <h2>Visiting</h2>
              <p>
                Use <code>!visit @username</code> to move your entity near another entity. Great for hanging out
                with friends in the terrarium.
              </p>

              <h2>Dancing & Singing</h2>
              <p>
                Insect users can use <code>!dance</code> to make their insect bust a move, or <code>!sing</code>
                to chirp with floating music notes. Great for celebrating and having fun in chat!
              </p>

              <h2>Petting & Nuzzling</h2>
              <p>
                Use <code>!pet @username</code> to pet someone's insect — hearts float up! Insects can also
                <code>!nuzzle @username</code> to snuggle up to another entity.
              </p>

              <h2>Plant Fun</h2>
              <p>
                Plants can <code>!shake</code> to wiggle around.
                Use <code>!wave</code> for a friendly wave to the stream!
              </p>

              <h2>Chat Bubbles</h2>
              <p>
                When a viewer who has an entity in the terrarium sends any chat message, it appears as a
                speech bubble above their plant or insect. This makes the terrarium feel alive and connected
                to the chat.
              </p>

            </article>
          )}

          {activeSection === 'bits-monetization' && (
            <article>
              <h1>Bits & Shop</h1>
              <p>
                Stream Terrarium includes a <strong>Twitch Extension Panel</strong> where viewers can use
                Bits to unlock cosmetic skins, trigger terrarium-wide effects, and earn persistent perks.
                
              </p>

              <h2>How It Works</h2>
              <ol className="help-steps">
                <li>The <strong>Terrarium panel</strong> appears below the stream when installed as a Twitch Extension</li>
                <li>Viewers browse three tabs: <strong>Cosmetics</strong>, <strong>Effects</strong>, and <strong>Perks</strong></li>
                <li>Clicking an item opens the Twitch Bits dialog</li>
                <li>After redemption, rewards apply instantly on the overlay</li>
              </ol>

              <h2>Tier 1 — Cosmetic Unlocks</h2>
              <p>One-time unlocks that permanently grant skins and features. Equip with <code>!equip</code> in chat.</p>
              <table className="help-table">
                <thead>
                  <tr><th>Bits</th><th>Item</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td>50</td><td>Golden Pot</td><td>Shiny gold pot for your plant</td></tr>
                  <tr><td>100</td><td>Neon / Crystal / Galaxy Plant</td><td>Glowing, sparkling, or cosmic skin for your plant</td></tr>
                  <tr><td>100</td><td>Golden / Rainbow / Shadow Insect</td><td>Gold, rainbow cycling, or dark shadow skin for your insect</td></tr>
                  <tr><td>250</td><td>Custom Name Color</td><td>Colored username above your entity</td></tr>
                  <tr><td>500</td><td>Legendary Plants</td><td>Cherry Blossom, Crystal Tree, or Venus Flytrap</td></tr>
                  <tr><td>500</td><td>Legendary Insects</td><td>Moth, Praying Mantis, or Scorpion</td></tr>
                </tbody>
              </table>

              <h2>Tier 2 — Instant Effects</h2>
              <p>One-time use effects that trigger immediately on the terrarium for all viewers to see.</p>
              <table className="help-table">
                <thead>
                  <tr><th>Bits</th><th>Effect</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td>1</td><td>Rain</td><td>Waters ALL plants in the terrarium for 30 seconds</td></tr>
                  <tr><td>50</td><td>Fireworks</td><td>Fireworks burst over the terrarium</td></tr>
                  <tr><td>100</td><td>Mega Bloom</td><td>Instantly blooms your plant to 100%</td></tr>
                  <tr><td>200</td><td>Butterfly Swarm</td><td>10 butterflies swarm across the terrarium for 30 seconds</td></tr>
                  <tr><td>500</td><td>Earthquake</td><td>Shakes the entire terrarium</td></tr>
                </tbody>
              </table>

              <h2>Tier 3 — Persistent Perks</h2>
              <p>Unlocked automatically when your <strong>cumulative Bits used</strong> reaches the threshold. These are permanent.</p>
              <table className="help-table">
                <thead>
                  <tr><th>Total Bits</th><th>Perk</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td>500</td><td>Second Plant Slot</td><td>Grow 2 plants at once</td></tr>
                  <tr><td>1,000</td><td>Second Insect Slot</td><td>Have 2 insects at once</td></tr>
                  <tr><td>2,500</td><td>VIP Badge</td><td>Star icon next to your username</td></tr>
                  <tr><td>5,000</td><td>Custom Plant Name</td><td>Name your plant anything with <code>!rename</code></td></tr>
                </tbody>
              </table>

              <h2>Bits Chat Commands</h2>
              <table className="help-table">
                <thead>
                  <tr><th>Command</th><th>Description</th><th>Example</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!shop [category]</code></td><td>Browse the bits shop</td><td><code>!shop effects</code></td></tr>
                  <tr><td><code>!mybits</code></td><td>Check your total bits used</td><td><code>!mybits</code></td></tr>
                  <tr><td><code>!mycosmetics</code></td><td>List your unlocked cosmetics</td><td><code>!mycosmetics</code></td></tr>
                  <tr><td><code>!equip [name]</code></td><td>Equip a cosmetic skin or legendary type</td><td><code>!equip golden_pot</code> or <code>!equip moth</code></td></tr>
                  <tr><td><code>!unequip</code></td><td>Remove your current cosmetic</td><td><code>!unequip</code></td></tr>
                  <tr><td><code>!rename [name]</code></td><td>Rename your plant (5,000 bits perk)</td><td><code>!rename Mr. Sprout</code></td></tr>
                </tbody>
              </table>

              <div className="help-tip">
                <strong>Tip:</strong> Cosmetics redeemed through the extension panel are unlocked instantly.
                Use <code>!equip</code> in chat to apply skins to your plant or insect. Use <code>!mycosmetics</code> to see what you own.
              </div>
            </article>
          )}

          {activeSection === 'admin-commands' && (
            <article>
              <h1>Admin & Moderator Commands</h1>
              <p>
                These commands are available to the broadcaster and moderators (configurable in settings).
              </p>

              <h2>Entity Management</h2>
              <table className="help-table">
                <thead>
                  <tr><th>Command</th><th>Description</th><th>Example</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!addplant @user [type]</code></td><td>Add a plant for a specific viewer</td><td><code>!addplant @viewer1 rose</code></td></tr>
                  <tr><td><code>!addinsect @user [type]</code></td><td>Add an insect for a specific viewer</td><td><code>!addinsect @viewer2 butterfly</code></td></tr>
                  <tr><td><code>!removeentity @user</code></td><td>Remove a viewer's plant or insect</td><td><code>!removeentity @viewer1</code></td></tr>
                  <tr><td><code>!removeplant @user</code></td><td>Remove a specific plant</td><td><code>!removeplant @viewer1</code></td></tr>
                  <tr><td><code>!removeinsect @user</code></td><td>Remove a specific insect</td><td><code>!removeinsect @viewer2</code></td></tr>
                </tbody>
              </table>

              <h2>Bulk Actions</h2>
              <table className="help-table">
                <thead>
                  <tr><th>Command</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>!waterall</code></td><td>Water every plant in the terrarium at once</td></tr>
                  <tr><td><code>!clearterrarium</code></td><td>Remove all plants and insects (use with caution!)</td></tr>
                  <tr><td><code>!cleargarden</code></td><td>Same as !clearterrarium</td></tr>
                </tbody>
              </table>

              <h2>Settings Panel</h2>
              <p>
                The Settings panel (click "Settings" button on the overlay) gives you full control over
                growth rates, visual options, permissions, insect roaming, display toggles, and more.
                Changes are saved to the server and persist across sessions.
              </p>
            </article>
          )}

          {activeSection === 'obs-setup' && (
            <article>
              <h1>OBS Setup Guide</h1>

              <h2>Adding the Browser Source</h2>
              <ol className="help-steps">
                <li>Open OBS Studio (or Streamlabs, XSplit, etc.)</li>
                <li>Click the <strong>+</strong> button in the Sources panel</li>
                <li>Select <strong>Browser</strong></li>
                <li>Name it "Stream Terrarium" and click OK</li>
                <li>Paste your overlay URL into the URL field</li>
                <li>Set <strong>Width</strong> to your stream width (e.g. 1920)</li>
                <li>Set <strong>Height</strong> to ~500px (adjustable)</li>
                <li>Check <strong>"Custom CSS"</strong> is empty or has the default</li>
                <li>Click OK — the terrarium appears!</li>
              </ol>

              <h2>Recommended Settings</h2>
              <div className="help-tip">
                <strong>Important OBS Settings:</strong>
                <ul>
                  <li>Uncheck "Shutdown source when not visible" — keeps the WebSocket connection alive between scenes</li>
                  <li>Uncheck "Refresh browser when scene becomes active" — prevents disconnects</li>
                  <li>Position the source at the bottom of your scene for the best look</li>
                </ul>
              </div>

              <h2>Transparent Background</h2>
              <p>
                The overlay has a transparent background by default, so it layers cleanly over your stream.
                If you want a completely minimal look, go to the <strong>Display</strong> tab in Settings
                and disable the background, glass frame, ground, and decorations to show only plants and insects.
              </p>

              <h2>Streamlabs & XSplit</h2>
              <p>
                Stream Terrarium works with any streaming software that supports Browser Sources.
                The setup process is the same — just paste your overlay URL as a Browser Source.
              </p>
            </article>
          )}

          {activeSection === 'display-settings' && (
            <article>
              <h1>Display Settings</h1>
              <p>
                The <strong>Display</strong> tab in the Settings panel lets you toggle individual scene elements
                on and off. This is great for customizing the look of your terrarium overlay.
              </p>

              <h2>Available Toggles</h2>
              <table className="help-table">
                <thead>
                  <tr><th>Setting</th><th>What it controls</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Show Ground / Soil</strong></td><td>The ground and soil layers where plants sit</td></tr>
                  <tr><td><strong>Show Glass Frame</strong></td><td>The glass terrarium border and shine overlay</td></tr>
                  <tr><td><strong>Show Water Particles</strong></td><td>Water drop animation when plants are watered</td></tr>
                  <tr><td><strong>Show Event Notifications</strong></td><td>Popup notifications for joins, waters, pollinations</td></tr>
                  <tr><td><strong>Show Chat Bubbles</strong></td><td>Speech bubbles above entities when viewers chat</td></tr>
                </tbody>
              </table>

              <div className="help-tip">
                <strong>Minimal Mode:</strong> Turn off Ground and Glass Frame
                to get a clean overlay with just plants and insects floating on your stream.
              </div>
            </article>
          )}

          {activeSection === 'faq' && (
            <article>
              <h1>Frequently Asked Questions</h1>

              <div className="help-faq">
                <h3>Is Stream Terrarium really free?</h3>
                <p>Yes! The overlay is 100% free. The optional Bits shop is an add-on for viewers who want cosmetics and effects — powered by Twitch In-Extension Bits.</p>

                <h3>Do plants grow when I'm offline?</h3>
                <p>Growth continues, but <strong>health decay pauses when the stream is offline</strong> by default. This means plants won't die while you're away. You can toggle this in Settings &gt; Growth.</p>

                <h3>How fast do plants die without water?</h3>
                <p>At the default rate (0.5% per minute), a plant takes about 3.3 hours of unwatered stream time to die. You can adjust this in the Growth settings — lower values make plants hardier.</p>

                <h3>Can a viewer be both a plant and an insect?</h3>
                <p>By default, each viewer has one entity. However, viewers who reach <strong>500 total Bits</strong> unlock a second plant slot, and <strong>1,000 total Bits</strong> unlocks a second insect slot.</p>

                <h3>How many entities can the terrarium hold?</h3>
                <p>By default, up to 50 plants and 30 insects. These limits are adjustable in the Settings panel under Growth.</p>

                <h3>Can I restrict joining to subscribers only?</h3>
                <p>Yes! In the Permissions tab, enable "Require Subscription to Join" and set the minimum tier (Tier 1, 2, or 3).</p>

                <h3>What happens when a plant reaches 100% growth?</h3>
                <p>The viewer can use <code>!bloom</code> for a special bloom animation, or <code>!harvest</code> to reset and grow again.</p>

                <h3>How do I use Bits to unlock items?</h3>
                <p>Viewers click the <strong>Terrarium panel</strong> below the stream to browse cosmetics, effects, and perks. Twitch's In-Extension Bits dialog appears — Twitch handles the transaction.</p>

                <h3>How do I equip a cosmetic I unlocked?</h3>
                <p>Type <code>!mycosmetics</code> to see what you own, then <code>!equip golden_pot</code> to apply a skin, or <code>!equip moth</code> to transform into a legendary type. Use <code>!unequip</code> to remove skins.</p>

                <h3>What are Legendary plants and insects?</h3>
                <p>Exclusive entity types (Cherry Blossom, Crystal Tree, Venus Flytrap, Moth, Mantis, Scorpion) that can only be unlocked by using 500 Bits each. Once unlocked, use <code>!join plant cherry_blossom</code> to grow one.</p>

                <h3>What is the VIP Badge?</h3>
                <p>A star icon that appears next to your username when you reach 2,500 cumulative Bits used. It's a mark of prestige in the terrarium community.</p>

                <h3>Can I rename my plant?</h3>
                <p>Yes! At 5,000 cumulative Bits, you unlock the <code>!rename</code> command. Type <code>!rename Mr. Sprout</code> to give your plant a custom name (max 20 characters).</p>

                <h3>Can I use this with Streamlabs instead of OBS?</h3>
                <p>Yes, Stream Terrarium works with any streaming software that supports Browser Sources — OBS, Streamlabs, XSplit, and more.</p>

                <h3>How do I reset the terrarium?</h3>
                <p>Use <code>!clearterrarium</code> in chat (broadcaster/mod only), or use the "Clear Terrarium" button in the Admin tab of Settings.</p>
              </div>
            </article>
          )}

          {activeSection === 'changelog' && (
            <article>
              <h1>Changelog</h1>
              <p>A history of updates and new features added to Stream Terrarium.</p>

              <div className="help-changelog">
                <div className="changelog-entry">
                  <h2>v1.5.0 <span className="changelog-date">March 2026</span></h2>
                  <h3>New Insects</h3>
                  <ul>
                    <li>Added <strong>Worm</strong> — a wiggly pink crawling insect</li>
                    <li>Added <strong>Moth</strong> — a patterned flying insect with dust particles (legendary)</li>
                    <li>Added <strong>Praying Mantis</strong> — animated claws and praying arms (legendary)</li>
                    <li>Added <strong>Scorpion</strong> — segmented tail with pulsing stinger (legendary)</li>
                  </ul>
                  <h3>New Plants</h3>
                  <ul>
                    <li>Added <strong>Cherry Blossom</strong> — branches with pink blossoms and falling petals (legendary)</li>
                    <li>Added <strong>Crystal Tree</strong> — faceted trunk with prismatic crystal shards (legendary)</li>
                    <li>Added <strong>Venus Flytrap</strong> — carnivorous plant with snapping trap heads (legendary)</li>
                  </ul>
                  <h3>Health & Growth</h3>
                  <ul>
                    <li>Added <strong>Pause health decay when offline</strong> — plants no longer die while the stream is off (enabled by default)</li>
                    <li>Increased plant survivability — health decay rate now supports fractional values for finer control</li>
                    <li>Improved growth timer precision for more consistent plant updates</li>
                  </ul>
                  <h3>New Commands</h3>
                  <ul>
                    <li><code>!wave</code> — wave at everyone (works for both plants and insects)</li>
                    <li><code>!sing</code> — make your insect sing with floating music notes</li>
                    <li><code>!nuzzle @user</code> — nuzzle up to another entity with heart animation</li>
                    <li><code>!pet @user</code> — pet an insect with floating hearts</li>
                  </ul>
                  <h3>UI & Settings</h3>
                  <ul>
                    <li>Added <strong>Bits tab</strong> to Settings panel for monetization controls</li>
                    <li>Added <strong>Changelog tab</strong> to Settings panel</li>
                    <li>Updated Help tab with complete command reference</li>
                    <li>Fixed Settings panel tab overflow when many tabs are visible</li>
                    <li>Subscription status notifications now auto-dismiss after 5 seconds</li>
                  </ul>
                  <h3>Twitch Extension</h3>
                  <ul>
                    <li>Added <strong>Terrarium panel extension</strong> — installable below the stream on Twitch</li>
                    <li>Extension panel with Cosmetics, Effects, and Perks tabs</li>
                  </ul>
                </div>

                <div className="changelog-entry">
                  <h2>v1.4.0 <span className="changelog-date">February 2026</span></h2>
                  <h3>Multi-Entity System</h3>
                  <ul>
                    <li>Viewers can now join as either a <strong>plant</strong> or an <strong>insect</strong></li>
                    <li>Added 9 insect types: Ladybug, Butterfly, Bee, Caterpillar, Firefly, Beetle, Dragonfly, Snail</li>
                    <li>Insects roam the terrarium autonomously with configurable speed and boundaries</li>
                    <li>Flying insects (butterfly, bee, firefly, dragonfly) can use <code>!fly</code></li>
                    <li>Crawling insects (ladybug, caterpillar, beetle, snail) walk along the ground</li>
                  </ul>
                  <h3>Insect Interactions</h3>
                  <ul>
                    <li><code>!pollinate @user</code> — insects can help plants grow faster</li>
                    <li><code>!visit @user</code> — move near another entity</li>
                    <li><code>!dance</code> — make your insect dance</li>
                    <li><code>!land @user</code> — land on a specific plant</li>
                  </ul>
                  <h3>Display Settings</h3>
                  <ul>
                    <li>Added toggles for ground, glass frame, water particles, notifications, chat bubbles</li>
                    <li>Added insect roaming speed and boundary controls</li>
                  </ul>
                </div>

                <div className="changelog-entry">
                  <h2>v1.3.0 <span className="changelog-date">January 2026</span></h2>
                  <h3>Settings Panel</h3>
                  <ul>
                    <li>Full settings panel with Growth, Visual, Display, Permissions, Insects, Admin, and Help tabs</li>
                    <li>Settings persist to the server and sync across sessions</li>
                    <li>Admin tab with entity management, quick actions, and active entity lists</li>
                  </ul>
                  <h3>Permissions</h3>
                  <ul>
                    <li>Subscription gating — require Tier 1, 2, or 3 sub to join</li>
                    <li>Configurable mod permissions for admin commands</li>
                    <li>Toggle individual features (watering, pollinating, visiting, bloom)</li>
                  </ul>
                </div>

                <div className="changelog-entry">
                  <h2>v1.2.0 <span className="changelog-date">December 2025</span></h2>
                  <h3>Chat Bubbles</h3>
                  <ul>
                    <li>Messages from viewers with entities appear as speech bubbles above their plant or insect</li>
                    <li>Toggleable in Display settings</li>
                  </ul>
                  <h3>Notifications</h3>
                  <ul>
                    <li>Event notification popups for joins, waters, pollinations, and harvests</li>
                    <li>Slide-in animations with auto-dismiss</li>
                  </ul>
                </div>

                <div className="changelog-entry">
                  <h2>v1.1.0 <span className="changelog-date">November 2025</span></h2>
                  <h3>Plant Variety</h3>
                  <ul>
                    <li>Expanded to 12 plant types: Flower, Mushroom, Cactus, Sunflower, Tulip, Rose, Tree, Fern, Succulent, Bamboo, Vine, Bonsai</li>
                    <li>Each plant has unique CSS art and growth animations</li>
                    <li>Added <code>!bloom</code> and <code>!harvest</code> commands</li>
                    <li>Added <code>!shake</code> for plant fun</li>
                  </ul>
                </div>

                <div className="changelog-entry">
                  <h2>v1.0.0 <span className="changelog-date">October 2025</span></h2>
                  <h3>Initial Release</h3>
                  <ul>
                    <li>Twitch chat-powered terrarium overlay</li>
                    <li>Viewers join with <code>!join plant</code> to grow plants</li>
                    <li>Community watering with <code>!water</code></li>
                    <li>Plants grow over time and persist in PostgreSQL database</li>
                    <li>OBS Browser Source integration with transparent background</li>
                    <li>Twitch EventSub WebSocket for real-time chat interaction</li>
                  </ul>
                </div>
              </div>
            </article>
          )}
        </main>
      </div>

      <style>{`
        .help-page {
          min-height: 100vh;
          background: #050a0f;
          color: #e0e0e0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        html, body, #root {
          overflow: visible !important;
          overflow-y: auto !important;
          height: auto !important;
          min-height: 100vh;
        }

        .help-nav {
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(5, 10, 15, 0.9);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          padding: 16px 24px;
        }

        .help-nav-inner {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .help-back-link {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #10b981;
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 500;
          transition: opacity 0.2s;
        }

        .help-back-link:hover {
          opacity: 0.8;
        }

        .help-brand {
          font-weight: 700;
          font-size: 1rem;
          color: #fff;
        }

        .help-layout {
          display: grid;
          grid-template-columns: 220px 1fr;
          max-width: 1200px;
          margin: 0 auto;
          gap: 0;
          min-height: calc(100vh - 60px);
        }

        .help-sidebar {
          position: sticky;
          top: 60px;
          height: fit-content;
          padding: 32px 16px;
          border-right: 1px solid rgba(255, 255, 255, 0.06);
        }

        .help-sidebar h3 {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: rgba(255, 255, 255, 0.4);
          margin-bottom: 12px;
        }

        .help-sidebar ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .help-sidebar li {
          margin-bottom: 2px;
        }

        .sidebar-link {
          display: block;
          width: 100%;
          text-align: left;
          padding: 8px 12px;
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.85rem;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.15s;
        }

        .sidebar-link:hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.05);
        }

        .sidebar-link.active {
          color: #10b981;
          background: rgba(16, 185, 129, 0.1);
          font-weight: 600;
        }

        .help-content {
          padding: 40px 48px;
          max-width: 800px;
        }

        .help-content h1 {
          font-size: 1.75rem;
          font-weight: 800;
          color: #fff;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .help-content h2 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #fff;
          margin-top: 32px;
          margin-bottom: 12px;
        }

        .help-content h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #fff;
          margin-top: 24px;
          margin-bottom: 8px;
        }

        .help-content p {
          font-size: 0.95rem;
          line-height: 1.7;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 12px;
        }

        .help-content ul, .help-content ol {
          padding-left: 20px;
          margin-bottom: 16px;
        }

        .help-content li {
          font-size: 0.95rem;
          line-height: 1.7;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 4px;
        }

        .help-content code {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.85rem;
          font-family: 'Fira Code', 'Cascadia Code', monospace;
        }

        .help-steps {
          counter-reset: step;
          list-style: none;
          padding: 0;
        }

        .help-steps li {
          counter-increment: step;
          padding: 12px 16px 12px 48px;
          position: relative;
          margin-bottom: 8px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }

        .help-steps li::before {
          content: counter(step);
          position: absolute;
          left: 16px;
          top: 14px;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          font-size: 0.75rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .help-tip {
          background: rgba(16, 185, 129, 0.05);
          border: 1px solid rgba(16, 185, 129, 0.15);
          border-radius: 10px;
          padding: 16px 20px;
          margin: 20px 0;
          font-size: 0.9rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.7);
        }

        .help-tip strong {
          color: #10b981;
        }

        .help-tip ul {
          margin-top: 8px;
          margin-bottom: 0;
        }

        .help-table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          font-size: 0.9rem;
        }

        .help-table th {
          text-align: left;
          padding: 10px 12px;
          background: rgba(255, 255, 255, 0.03);
          color: rgba(255, 255, 255, 0.5);
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .help-table td {
          padding: 10px 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
          color: rgba(255, 255, 255, 0.7);
        }

        .help-table tr:hover td {
          background: rgba(255, 255, 255, 0.02);
        }

        .help-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 12px;
          margin: 16px 0;
        }

        .help-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          padding: 16px;
          transition: border-color 0.2s;
        }

        .help-card:hover {
          border-color: rgba(16, 185, 129, 0.2);
        }

        .help-card h3 {
          font-size: 0.9rem;
          margin-top: 0;
          margin-bottom: 6px;
        }

        .help-card p {
          font-size: 0.8rem;
          margin-bottom: 8px;
        }

        .help-card code {
          font-size: 0.75rem;
        }

        .help-faq h3 {
          margin-top: 28px;
        }

        .help-faq p {
          margin-bottom: 0;
        }

        .help-changelog {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .changelog-entry {
          padding: 24px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        .changelog-entry:last-child {
          border-bottom: none;
        }

        .changelog-entry h2 {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .changelog-date {
          font-size: 0.75em;
          font-weight: 400;
          color: #888;
          background: rgba(255, 255, 255, 0.05);
          padding: 2px 10px;
          border-radius: 12px;
        }

        .changelog-entry h3 {
          font-size: 1rem;
          color: #9b7dff;
          margin-top: 16px;
          margin-bottom: 8px;
        }

        .changelog-entry ul {
          margin: 0 0 8px 0;
          padding-left: 20px;
        }

        .changelog-entry li {
          margin-bottom: 4px;
          line-height: 1.5;
          color: #ccc;
        }

        @media (max-width: 768px) {
          .help-layout {
            grid-template-columns: 1fr;
          }

          .help-sidebar {
            position: static;
            border-right: none;
            border-bottom: 1px solid rgba(255, 255, 255, 0.06);
            padding: 16px;
          }

          .help-sidebar ul {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          }

          .help-content {
            padding: 24px 16px;
          }

          .help-card-grid {
            grid-template-columns: 1fr;
          }

          .help-table {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </div>
  )
}

export default Help